/**
 * Grouping tape prints into the packages they were executed as.
 *
 * Two facts about the feed shape this:
 *
 *  1. The exchange flags multi-leg prints by condition code, so membership does not have to be
 *     guessed from size or premium similarity.
 *  2. Legs of one package cross on one exchange at one instant. The tape's `timestamp` is the
 *     exchange print time to the millisecond, and `sequence` is the OPRA sequence — together they
 *     identify the package without any tolerance for the user to tune.
 *
 * Note this deliberately does not use `flow_buffer.timestamp`, which is ingest time and orders rows
 * by when they were written rather than when they traded.
 */

import { multiLegInfo, MultiLegInfo } from './opraConditions';

export interface LegRowLike {
  id?: number;
  timestamp?: string;
  time?: string;
  condition?: number | null;
  condition_codes?: number[] | null;
  condition_name?: string;
  sequence?: number | null;
  exchange?: number | null;
  contract_ticker?: string;
  strike?: number;
  expiry?: string;
  optionType?: string;
  price?: number;
  volume?: number;
  quantity?: number;
  premium?: number;
  side?: string;
  execution_side?: string;
}

/** Exchange print time in ms, or NaN. Never falls back to an ingest clock. */
export function printTimeMs(row: LegRowLike): number {
  const raw = row.timestamp;
  if (!raw) return NaN;
  const s = String(raw).trim();
  // Values arrive without a zone and are already exchange-local; parsing as local keeps prints that
  // share a clock sharing a key, which is all this is used for.
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  const t = d.getTime();
  return Number.isNaN(t) ? NaN : t;
}

/**
 * Identity of a single real print. The feed re-ingests the same trade many times over, so rows must
 * be collapsed on what the exchange assigned rather than on anything we stamped.
 */
export function printIdentity(row: LegRowLike): string {
  const seq = row.sequence ?? 'noseq';
  const exch = row.exchange ?? 'noexch';
  const contract = row.contract_ticker ?? `${row.strike}${row.optionType}${row.expiry}`;
  // Sequence is unique per print per exchange; contract guards the case where it is missing.
  return `${exch}|${seq}|${contract}|${row.price ?? ''}|${row.volume ?? row.quantity ?? ''}`;
}

/** Collapse re-ingested copies, keeping first occurrence order. */
export function dedupePrints<T extends LegRowLike>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of rows) {
    const key = printIdentity(r);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/** Package key: one exchange, one instant. Null when the print is not part of a package. */
export function packageKey(row: LegRowLike): string | null {
  if (!multiLegInfo(row)) return null;
  const t = printTimeMs(row);
  if (Number.isNaN(t)) return null;
  const exch = row.exchange ?? 'noexch';
  return `${exch}@${t}`;
}

export interface LegPackage<T extends LegRowLike = LegRowLike> {
  key: string;
  legs: T[];
  info: MultiLegInfo;
  /** Stable index used to pick a colour, so the same package keeps its colour across renders. */
  colorIndex: number;
}

/**
 * Packages present in these rows, keyed by exchange + print instant.
 *
 * A package needs more than one distinct contract: a single contract printing several times at one
 * instant is a sweep being reported in pieces, not a spread, and colouring it as one would be a lie.
 */
export function groupLegPackages<T extends LegRowLike>(rows: T[]): Map<string, LegPackage<T>> {
  const buckets = new Map<string, T[]>();
  for (const r of rows) {
    const key = packageKey(r);
    if (!key) continue;
    const list = buckets.get(key);
    if (list) list.push(r);
    else buckets.set(key, [r]);
  }

  const out = new Map<string, LegPackage<T>>();
  let colorIndex = 0;
  for (const [key, legs] of Array.from(buckets.entries())) {
    const contracts = new Set(
      legs.map((l) => l.contract_ticker ?? `${l.strike}${l.optionType}${l.expiry}`),
    );
    if (contracts.size < 2) continue;
    const info = multiLegInfo(legs[0]);
    if (!info) continue;
    out.set(key, { key, legs, info, colorIndex: colorIndex++ });
  }
  return out;
}

/**
 * Distinct colours for adjacent packages. Chosen to stay legible on the dark tape and to avoid the
 * up/down red-green that already means direction elsewhere in the table.
 */
export const LEG_PACKAGE_COLORS = [
  '#7c9cff',
  '#f0a35e',
  '#5ecfa8',
  '#c98bdb',
  '#e0c15c',
  '#6fc7d8',
  '#e08a9b',
  '#9db85e',
];

export function legPackageColor(colorIndex: number): string {
  return LEG_PACKAGE_COLORS[colorIndex % LEG_PACKAGE_COLORS.length];
}
